import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, Menu } from 'lucide-react';
import Logo from '../components/Logo';

const HERO_IMG = 'https://images.pexels.com/photos/4989171/pexels-photo-4989171.jpeg';

const NAV = ['Product', 'Solutions', 'Pricing', 'Security', 'Resources'];

const FEATURES = [
  'Built for Uganda',
  'Low-bandwidth ready',
  'Secure role-based access',
  'Multi-facility support',
  'QR patient identity',
  'FHIR-ready',
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 h-[72px] flex items-center justify-between">
          <Logo size={38} />
          <nav className="hidden lg:flex items-center gap-9">
            {NAV.map((n) => (
              <a
                key={n}
                href="#"
                onClick={(e) => e.preventDefault()}
                className="text-[15px] font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                {n}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/login')}
              className="hidden sm:inline text-[15px] font-medium text-slate-700 hover:text-slate-900 transition-colors"
            >
              Sign in
            </button>
            <Button
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-5 h-11 text-[15px] font-semibold shadow-sm"
            >
              Start free trial
            </Button>
            <button className="lg:hidden text-slate-700"><Menu size={22} /></button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1280px] mx-auto px-6 pt-16 pb-8">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-fade-in-up">
            <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 px-4 py-1.5 text-sm font-bold">
              Built for Uganda · Ready for East Africa
            </span>
            <h1 className="font-display font-extrabold tracking-tight mt-6 text-[56px] leading-[1.02] text-slate-900">
              Digitize Pharmacy.
              <br />
              <span className="text-blue-600">Dispense with Care.</span>
            </h1>
            <p className="mt-6 text-[19px] leading-relaxed text-slate-500 max-w-xl">
              Vitality Pharmacy Software connects prescriptions, stock batches,
              FEFO dispensing, billing and multi-branch operations through one
              secure platform.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                onClick={() => navigate('/login')}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-7 h-14 text-[16px] font-semibold shadow-sm"
              >
                Start free trial
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-slate-300 text-slate-800 hover:bg-slate-50 rounded-lg px-7 h-14 text-[16px] font-semibold bg-white"
              >
                Book demo
              </Button>
            </div>
            <div className="mt-12 flex items-center gap-12">
              {[
                { n: '135', l: 'Pharmacies live' },
                { n: '412K+', l: 'Prescriptions' },
                { n: '99.98%', l: 'Uptime' },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display font-extrabold text-4xl text-slate-900">{s.n}</div>
                  <div className="text-slate-500 text-[15px] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero image with floating card */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden bg-slate-100 shadow-xl">
              <img
                src={HERO_IMG}
                alt="Pharmacist working"
                className="w-full h-[520px] object-cover"
              />
            </div>
            <div className="absolute left-6 right-6 bottom-6 bg-white/95 backdrop-blur rounded-2xl shadow-lg p-4 flex items-center gap-4 animate-floaty">
              <div className="h-11 w-11 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                <CheckCircle2 className="text-green-600" size={24} />
              </div>
              <div>
                <div className="font-display font-bold text-slate-900">RX 2401-0007 · Nakato Sarah</div>
                <div className="text-slate-500 text-sm">Prescription verified · FEFO batch · Bill settled</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="border-t border-slate-100 bg-slate-50/60 mt-8">
        <div className="max-w-[1280px] mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-y-4">
          {FEATURES.map((f) => (
            <div key={f} className="flex items-center gap-2 text-slate-700">
              <CheckCircle2 className="text-green-600" size={20} />
              <span className="text-[15px] font-medium">{f}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100">
        <div className="max-w-[1280px] mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={34} />
          <p className="text-slate-400 text-sm">© 2026 Vitality Software Limited · Kampala, Uganda</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
